
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import TestimonialsSection from '../home/components/TestimonialsSection';

export default function About() {
  const milestones = [
    {
      year: '2024',
      title: 'Company Founded',
      description: 'Osem Dynamics LLC was established with a vision to revolutionize social media management.',
    },
    {
      year: '2025',
      title: 'Beta Launch',
      description: 'MandaPost enters beta testing with select partners and early adopters.',
    },
    {
      year: 'July 2025',
      title: 'Official Launch',
      description: 'First social media planning platform powered by Agentic AI goes live.',
    },
  ];

  const features = [
    {
      icon: 'ri-robot-line',
      title: 'Agentic AI',
      description: 'Generates platform-tailored captions, images, and strategic content ideas, learning and adapting to each brand\'s style and goals.',
    },
    {
      icon: 'ri-share-line',
      title: 'Omni Sharing',
      description: 'Schedule and post to Instagram, Facebook, X (Twitter), LinkedIn, TikTok, Lemon8, and Blue Sky — all from one dashboard.',
    },
    {
      icon: 'ri-folder-user-line',
      title: 'Multi-Account Organization',
      description: 'Easily manage multiple brands or client accounts, keeping content strategies and performance analytics in one place.',
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Smart Formatting',
      description: 'Automatically adjusts content to fit the best practices of each platform, saving hours of manual editing.',
    },
  ];

  const team = [
    {
      role: 'Leadership',
      description: 'Led by experienced entrepreneurs and technologists passionate about simplifying social media management.',
    },
    {
      role: 'Engineering',
      description: 'Our AI and development team pushes the boundaries of what\'s possible with agentic intelligent technology.',
    },
    {
      role: 'Customer Success',
      description: 'Dedicated to helping our users maximize their social media impact and achieve their goals.',
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#e4f4ff]/30 via-white to-[#b3d9ff]/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#66b3ff] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#b3d9ff] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/40 rounded-full text-[#22577A] font-medium mb-6">
              <i className="ri-building-line mr-2"></i>
              About Osem Dynamics LLC
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-7xl font-bold text-[#22577A] mb-6 leading-tight">
              Ushering in a New Era of
              <span className="block bg-gradient-to-r from-[#38a3a5] to-[#66b3ff] bg-clip-text text-transparent">
                Intelligent Social Media
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 mb-10 leading-relaxed">
              We're building the future of social media management with the world's first planning platform powered by Agentic AI.
            </p>
          </div>
        </div>
      </section>

      {/* Press Release Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#e4f4ff]/20 to-[#b3d9ff]/20 rounded-2xl border-2 border-[#66b3ff]/30 p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center">
                <i className="ri-newspaper-line text-white text-2xl"></i>
              </div>
              <div>
                <div className="text-sm text-gray-600 font-medium">PRESS RELEASE</div>
                <div className="text-lg font-bold text-[#22577A]">WASHINGTON, DC — July 11th, 2025</div>
              </div>
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-[#22577A] mb-6 leading-tight">
              Osem Dynamics LLC Launches The First Social Media Planning Platform Powered by Agentic AI Named MandaPost
            </h2>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>MandaPost</strong>, a groundbreaking social media and marketing planning platform. MandaPost is the first planning software to feature agentic AI, ushering in a new era of intelligent, hands-off social media management.
              </p>

              <div className="bg-white rounded-xl p-6 border-2 border-[#66b3ff]/20 mb-6">
                <p className="text-gray-700 leading-relaxed italic">
                  "We saw firsthand how much time was wasted juggling content calendars, formatting posts, and trying to maintain a consistent brand voice across multiple platforms," said a spokesperson from Osem Dynamics. "MandaPost solves that problem by putting a smart, AI-driven planner in your hands."
                </p>
              </div>

              <h3 className="text-2xl font-bold text-[#22577A] mb-4">
                MandaPost's Robust Platform Includes:
              </h3>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-lg transition-all"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center mb-4">
                      <i className={`${feature.icon} text-white text-2xl`}></i>
                    </div>
                    <h4 className="text-lg font-bold text-[#22577A] mb-2">{feature.title}</h4>
                    <p className="text-gray-700 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-xl p-6 border-2 border-[#66b3ff]/20 mb-6">
                <p className="text-gray-700 leading-relaxed italic">
                  "Our goal with MandaPost is to simplify the entire process. With agentic AI, users can plan, create, and publish posts across all platforms without losing the personal touch or spending endless hours creating it."
                </p>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                MandaPost is now available to small business owners, marketing teams, and agencies seeking to streamline their social media efforts while elevating their online presence with cutting-edge intelligent tools.
              </p>

              <div className="bg-gradient-to-r from-[#22577A] to-[#38a3a5] rounded-xl p-6 text-white mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-gift-line text-2xl"></i>
                  <h4 className="text-xl font-bold">Special Launch Offer</h4>
                </div>
                <p className="text-white/90 text-lg">
                  We're currently in our first rounds of beta testing and will be offering <strong>the first three months free</strong> with our initial launch to the public.
                </p>
              </div>

              <div className="border-t-2 border-gray-200 pt-6">
                <h4 className="font-bold text-[#22577A] mb-3">Contact Information</h4>
                <p className="text-gray-700 mb-2">
                  <strong>For press inquiries:</strong>{' '}
                  <a href="mailto:hello@mandapost.com" className="text-[#38a3a5] hover:underline">
                    hello@mandapost.com
                  </a>
                </p>
                <p className="text-gray-700">
                  <strong>More information:</strong>{' '}
                  <a href="https://www.mandapost.com" className="text-[#38a3a5] hover:underline">
                    www.MandaPost.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From vision to reality: Building the future of social media management
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-[#22577A] to-[#66b3ff]"></div>

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white rounded-xl p-6 border-2 border-[#66b3ff]/30 hover:shadow-xl transition-all">
                      <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-full font-semibold mb-3">
                        {milestone.year}
                      </div>
                      <h3 className="text-2xl font-bold text-[#22577A] mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {milestone.description}
                      </p>
                    </div>
                  </div>

                  {/* Timeline Dot */}
                  <div className="hidden md:block w-6 h-6 bg-gradient-to-br from-[#22577A] to-[#66b3ff] rounded-full border-4 border-white shadow-lg z-10"></div>

                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-compass-3-line text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-white/90 text-lg leading-relaxed">
                To empower businesses, creators, and agencies with intelligent intelligent tools that simplify social media management, amplify their voice, and drive meaningful engagement—without the overwhelm.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-eye-line text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-white/90 text-lg leading-relaxed">
                To be the world's leading AI-powered social media platform, where every user—from solopreneurs to global brands—can effortlessly create, manage, and optimize their online presence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Meet the Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Passionate innovators dedicated to transforming social media management
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-2xl p-8 border-2 border-[#66b3ff]/30 hover:shadow-xl transition-all text-center"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-team-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#22577A] mb-3">{member.role}</h3>
                <p className="text-gray-700 leading-relaxed">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl border-2 border-[#66b3ff]/30 p-12 shadow-xl">
            <div className="w-16 h-16 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-rocket-line text-white text-3xl"></i>
            </div>
            <h2 className="text-4xl font-bold text-[#22577A] mb-4">
              Join Us on This Journey
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Be part of the revolution in social media management. Start your free trial today and experience the power of Agentic AI.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
              >
                Start Free Trial
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#66b3ff] text-[#22577A] rounded-xl font-semibold text-lg hover:bg-[#e4f4ff]/20 transition-all cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
